import React, { useEffect, useState } from "react";
import { SchoolOfferLink } from "@/components/SchoolOfferLink";
import { InfoTip } from "@/components/InfoTip";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crown, Check, ArrowLeft, ExternalLink } from "lucide-react";
import { useBilling } from "@/contexts/BillingContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BILLING } from "@/constants/testIds";
import { trackCheckoutStarted, logFunnel } from "@/lib/analytics";
import { RedeemOrgCode } from "@/components/RedeemOrgCode";

const eur = (n) => n.toFixed(2).replace(".", ",");

const PackButton = ({ pack, best, tag, busy, disabled, onClick, external }) => {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`upgrade-pack-${pack.id}`}
      className="group border border-[#002FA7] px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50"
    >
      <div>
        <div className="font-black text-xl" style={{ fontFamily: "Outfit" }}>
          {t("billing.pack_credits", { count: pack.credits })}
          {tag && (
            <span className="ml-2 align-middle font-mono uppercase tracking-[0.14em] text-[10px] px-2 py-0.5 bg-[#FF2A00] text-white">
              {tag}
            </span>
          )}
          {best && (
            <span className="ml-2 align-middle font-mono uppercase tracking-[0.14em] text-[10px] px-2 py-0.5 bg-[#002FA7] text-white group-hover:bg-white group-hover:text-[#002FA7]">
              {t("billing.pack_best")}
            </span>
          )}
        </div>
        <div className="text-xs opacity-70 mt-0.5">
          {t("billing.pack_per_summary", { price: eur(pack.amount / pack.credits) })}
        </div>
      </div>
      <div className="flex items-center gap-2 font-black text-2xl" style={{ fontFamily: "Outfit" }}>
        {busy ? t("billing.upgrade_redirecting") : `${eur(pack.amount)} €`}
        {external && !busy && <ExternalLink className="w-4 h-4" />}
      </div>
    </button>
  );
};

export default function Upgrade() {
  const { t } = useTranslation();
  const { billing, startCheckout, paymentsBlockedInTWA } = useBilling();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { logFunnel("pricing_view"); }, []);

  const isPro = !!billing?.is_pro;
  const packs = billing?.packs || [];
  const freeLimit = billing?.free_summary_limit ?? 3;
  const priceStr = (billing?.discovery_pack?.amount ?? packs[0]?.amount ?? 2).toFixed(2);

  const onCheckout = async (packId) => {
    setBusy(packId);
    trackCheckoutStarted();
    try {
      await startCheckout(packId);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || t("billing.checkout_failed")
      );
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={BILLING.upgradePage}
      className="border-t border-border min-h-[calc(100vh-64px)]"
    >
      <div className="px-6 md:px-10 py-6 border-b border-border">
        <Link
          to="/"
          className="font-mono uppercase tracking-[0.2em] text-[11px] flex items-center gap-2 hover:text-[#002FA7]"
        >
          <ArrowLeft className="w-3 h-3" /> {t("billing.upgrade_back")}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[70vh]">
        {/* Left — hero */}
        <section className="p-8 lg:p-16 border-r border-b border-border bg-[#002FA7] text-white flex flex-col justify-center">
          <div className="sux-marker-light mb-4">{t("billing.upgrade_page_marker")}</div>
          <h1
            className="text-5xl lg:text-7xl font-black leading-[0.92] mb-6"
            style={{ fontFamily: "Outfit" }}
          >
            {t("billing.upgrade_page_title_a")}<br />{t("billing.upgrade_page_title_b")}
          </h1>
          <p className="text-white/70 text-base leading-relaxed max-w-md mb-10">
            {t("billing.upgrade_page_subtitle")}
          </p>
          <div className="flex items-end gap-3">
            <span
              className="text-7xl font-black tracking-tight text-white"
              style={{ fontFamily: "Outfit" }}
            >
              {t("billing.upgrade_from", { price: priceStr.replace(".", ",") })}
            </span>
            <span className="sux-marker-light mb-3">
              {t("billing.upgrade_paid_once")}
            </span>
          </div>
        </section>

        {/* Right — features + CTA */}
        <section className="p-8 lg:p-16 border-b border-border flex flex-col">
          <div className="sux-marker mb-3 flex items-center gap-1">{t("billing.upgrade_what_you_get")} <InfoTip testid="upgrade-info" content={t("tips.upgrade", "Vous payez une seule fois, sans abonnement. Les crédits arrivent tout de suite et n'expirent jamais. Le coût de chaque résumé est affiché avant de le lancer.")} /></div>
          <h2
            className="text-3xl font-black tracking-tight mb-8"
            style={{ fontFamily: "Outfit" }}
          >
            {isPro
              ? t("billing.upgrade_already_pro")
              : t("billing.upgrade_beyond", { limit: freeLimit })}
          </h2>

          <ul className="space-y-4 mb-10">
            {[
              [t("billing.upgrade_feature_1"), t("billing.upgrade_feature_1_desc")],
              [t("billing.upgrade_feature_2"), t("billing.upgrade_feature_2_desc")],
              [t("billing.upgrade_feature_3"), t("billing.upgrade_feature_3_desc")],
              [t("billing.upgrade_feature_4"), t("billing.upgrade_feature_4_desc")],
            ].map(([h, p]) => (
              <li key={h} className="flex items-start gap-4">
                <div className="w-6 h-6 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-base">{h}</div>
                  <div className="text-muted-foreground text-sm">{p}</div>
                </div>
              </li>
            ))}
          </ul>

          {isPro && (
            <div className="border border-[#002FA7] bg-[#002FA7]/5 p-4 mb-4 flex items-center gap-3 text-sm">
              <Crown className="w-5 h-5 text-[#002FA7] flex-shrink-0" />
              <span>{t("billing.pro_legacy_note")}</span>
            </div>
          )}
          {paymentsBlockedInTWA && (
            <div
              data-testid={BILLING.twaNotice}
              className="border border-[#002FA7] bg-[#EEF3FF] text-[#002FA7] p-4 mb-4 text-sm leading-relaxed"
            >
              <div className="font-mono uppercase tracking-[0.2em] text-[10px] mb-2">
                {t("billing.upgrade_twa_notice_title")}
              </div>
              {t("billing.upgrade_twa_notice_body")}
            </div>
          )}
          <label
            data-testid="upgrade-terms-label"
            className="mt-auto mb-4 flex items-start gap-3 text-[13px] leading-relaxed cursor-pointer"
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="upgrade-terms-checkbox"
              className="mt-1 w-4 h-4 accent-[#002FA7] flex-shrink-0"
            />
            <span>
              {t("billing.terms_before", "J'accepte les")}{" "}
              <Link to="/cgv" target="_blank" className="underline text-[#002FA7]" data-testid="upgrade-cgv-link">
                {t("billing.terms_link", "conditions générales de vente")}
              </Link>{" "}
              {t("billing.terms_after", "et je demande l'accès immédiat à mes crédits : je reconnais renoncer à mon droit de rétractation de 14 jours dès leur mise à disposition.")}{" "}
              <InfoTip testid="upgrade-terms-info" content={t("tips.terms", "Obligation légale : comme vos crédits sont utilisables tout de suite, la loi vous demande d'accepter qu'il n'y ait pas de délai de 14 jours pour changer d'avis.")} />
            </span>
          </label>
          <div className="grid gap-3" data-testid="upgrade-packs">
            {billing?.discovery_pack && (
              <PackButton
                pack={billing.discovery_pack}
                tag={t("billing.pack_discovery_tag", "Découverte · 1 seule fois")}
                busy={busy === billing.discovery_pack.id}
                disabled={!!busy || !agreed}
                onClick={() => onCheckout(billing.discovery_pack.id)}
                external={paymentsBlockedInTWA}
              />
            )}
            {packs.map((p, i) => (
              <PackButton
                key={p.id}
                pack={p}
                best={i === packs.length - 1}
                busy={busy === p.id}
                disabled={!!busy || !agreed}
                onClick={() => onCheckout(p.id)}
                external={paymentsBlockedInTWA}
              />
            ))}
          </div>

          <p className="sux-marker mt-6">
            {paymentsBlockedInTWA
              ? t("billing.upgrade_secured_twa")
              : t("billing.upgrade_secured")}
          </p>
          <p className="text-xs text-muted-foreground mt-3" data-testid="upgrade-premium-note">
            {t("billing.premium_note", "Gemini 3 Flash : 1 crédit par résumé. Claude Sonnet 4.5 et GPT-5.2 (IA premium) : 2 crédits.")}
          </p>
          <RedeemOrgCode />
          <SchoolOfferLink testid="upgrade-school-offer-link" />
        </section>
      </div>
    </div>
  );
}
